import axios from 'axios';
import config from './config';
import { logger, parseJwt } from './util';
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY, USER_KEY } from './constants';

const params = new URLSearchParams(window.location.search);

const client = axios.create({
  baseURL: config.baseApiUrl,
});

client.interceptors.request.use((req) => {
  const wlCreator = params.get('wlCreator');
  const wlHost = params.get('wlHost');
  if (wlCreator && wlHost) {
    req.headers['x-whitelabel-creator'] = params.get('wlCreator');
    req.headers['x-whitelabel-host'] = params.get('wlHost');
  }
  return req;
});

client.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const { config, response } = error;
    const status = response?.status;
    const originalRequest = config;

    if (status === 429) {
      console.log('Too many requests, please try in some time');
      return Promise.reject(error);
    }
    if (status === 401) {
      if (originalRequest.isRetry) {
        return Promise.reject(error);
      }
      if (!isTokenRefreshing) {
        refreshToken();
      }
      const retryOrigReq = new Promise((resolve) => {
        subscribeTokenRefresh((token) => {
          // replace the expired token and retry
          originalRequest.headers.Authorization = `Bearer ${token}`;
          originalRequest.isRetry = true;
          resolve(axios(originalRequest));
        });
      });
      return retryOrigReq;
    }

    // if /get-access-token gets error, its probably best to just log out the user
    if (error.config?.url.includes('get-access-token')) {
      setLocalData(null);
    }
    if (status === 400) {
      console.log('error====>>>', error.config.url);
      console.log('Error>>>>>>', JSON.stringify(error.response));
      return Promise.reject(error);
    }

    console.log('unknown error', error.response);
    return Promise.reject(error);
  }
);

let isTokenRefreshing = false;
let pendingRequests = [];

const onRefreshed = (token) => {
  pendingRequests.map((cb) => cb(token));
  pendingRequests = [];
};

const subscribeTokenRefresh = (cb) => {
  pendingRequests.push(cb);
};

const fetchNewToken = () => {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  return client.post(
    `${config.baseApiUrl}/get-access-token`,
    {
      refreshToken,
    },
    { skipAuthRefresh: true }
  );
};

const setLocalData = (token) => {
  if (token) {
    const parsedUser = parseJwt(token);
    localStorage.setItem(USER_KEY, JSON.stringify(parsedUser));
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
    client.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    delete client.defaults.headers.common.Authorization;
  }
};

const refreshToken = async (callback, count = 0) => {
  isTokenRefreshing = true;
  fetchNewToken()
    .then((newToken) => {
      isTokenRefreshing = false;
      if (newToken?.data.result?.accessToken) {
        localStorage.setItem(
          REFRESH_TOKEN_KEY,
          newToken?.data.result.refreshToken || ''
        );
        setLocalData(newToken?.data.result?.accessToken);
        onRefreshed(newToken?.data.result.accessToken || '');

        if (callback) {
          setTimeout(() => {
            callback(newToken.data.result?.accessToken, count + 1);
          }, 300);
        }
      } else {
        setLocalData(null);
        onRefreshed('');
      }
    })
    .catch(() => {
      setLocalData(null, true);
      delete client.defaults.headers.common.Authorization;
      onRefreshed('');
    });
};

const request = async (options) => {
  try {
    const response = await client(options);
    return response.data;
  } catch (error) {
    logger.error('Request Failed:', error.config);
    if (error.response) {
      logger.error('Status:', error?.response?.status);
      logger.error('Data:', error?.response?.data);
      logger.error('Headers:', error?.response?.headers);
    } else {
      logger.error('Error Message:', error.message);
    }
    const errorMessage =
      error?.response?.data?.result?.message ||
      error?.response?.data?.result ||
      error?.message;
    const errorObject = new Error(errorMessage);
    errorObject.status = error?.response?.status;
    errorObject.data = error?.response?.data?.result;
    errorObject.response = error?.response?.data;
    throw errorObject;
  }
};

export default request;
